import {
  Email as EmailIcon,
  Person as PersonIcon,
  Security as SecurityIcon,
} from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { authHeaders } from '../utils/api'

interface CreateShareModalProps {
  open: boolean
  onClose: () => void
}

interface Vet {
  id: string
  name: string
  email: string
  organization?: string
  searchText: string
}

interface Pet {
  id: string
  name: string
  species: string
  owner: {
    name: string
  }
}

const getStringValue = (value: unknown): string => {
  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!normalized) {
      return ''
    }

    const lowered = normalized.toLowerCase()
    if (lowered === 'undefined' || lowered === 'null') {
      return ''
    }

    return normalized
  }
  if (typeof value === 'number') {
    return String(value)
  }
  return ''
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const getPathValue = (source: Record<string, unknown>, path: string[]): unknown => {
  let current: unknown = source
  for (const segment of path) {
    if (!isRecord(current)) {
      return undefined
    }
    current = current[segment]
  }
  return current
}

const getFirstStringFromPaths = (
  source: Record<string, unknown>,
  candidatePaths: string[][],
): string => {
  for (const path of candidatePaths) {
    const value = getStringValue(getPathValue(source, path))
    if (value) {
      return value
    }
  }
  return ''
}

const collectSearchStrings = (value: unknown, depth = 0): string[] => {
  if (depth > 3 || value === null || value === undefined) {
    return []
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const normalized = getStringValue(value)
    return normalized ? [normalized.toLowerCase()] : []
  }
  if (Array.isArray(value)) {
    return value.flatMap((item) => collectSearchStrings(item, depth + 1))
  }
  if (isRecord(value)) {
    return Object.values(value).flatMap((item) => collectSearchStrings(item, depth + 1))
  }
  return []
}

const normalizeVet = (rawVet: Record<string, unknown>): Vet | null => {
  const firstName = getFirstStringFromPaths(rawVet, [
    ['firstName'],
    ['first_name'],
    ['user', 'firstName'],
    ['user', 'first_name'],
    ['profile', 'firstName'],
    ['profile', 'first_name'],
    ['contact', 'firstName'],
    ['contact', 'first_name'],
  ])
  const lastName = getFirstStringFromPaths(rawVet, [
    ['lastName'],
    ['last_name'],
    ['user', 'lastName'],
    ['user', 'last_name'],
    ['profile', 'lastName'],
    ['profile', 'last_name'],
    ['contact', 'lastName'],
    ['contact', 'last_name'],
  ])
  const fullNameFromParts = [firstName, lastName].filter(Boolean).join(' ').trim()

  const id =
    getFirstStringFromPaths(rawVet, [
      ['id'],
      ['_id'],
      ['userId'],
      ['user_id'],
      ['vetId'],
      ['vet_id'],
      ['user', 'id'],
      ['user', '_id'],
      ['profile', 'id'],
      ['profile', '_id'],
    ]) ||
    getFirstStringFromPaths(rawVet, [
      ['email'],
      ['emailAddress'],
      ['email_address'],
      ['userEmail'],
      ['user', 'email'],
      ['user', 'emailAddress'],
      ['user', 'email_address'],
    ]) ||
    getFirstStringFromPaths(rawVet, [
      ['name'],
      ['fullName'],
      ['full_name'],
      ['displayName'],
      ['user', 'name'],
      ['user', 'fullName'],
      ['user', 'full_name'],
      ['user', 'displayName'],
    ])

  const name =
    getFirstStringFromPaths(rawVet, [
      ['name'],
      ['fullName'],
      ['full_name'],
      ['displayName'],
      ['user', 'name'],
      ['user', 'fullName'],
      ['user', 'full_name'],
      ['user', 'displayName'],
      ['profile', 'name'],
      ['profile', 'fullName'],
      ['profile', 'full_name'],
      ['profile', 'displayName'],
      ['contact', 'name'],
      ['contact', 'fullName'],
      ['contact', 'full_name'],
      ['contact', 'displayName'],
    ]) || fullNameFromParts

  const email = getFirstStringFromPaths(rawVet, [
    ['email'],
    ['emailAddress'],
    ['email_address'],
    ['userEmail'],
    ['user', 'email'],
    ['user', 'emailAddress'],
    ['user', 'email_address'],
    ['profile', 'email'],
    ['profile', 'emailAddress'],
    ['profile', 'email_address'],
    ['contact', 'email'],
    ['contact', 'emailAddress'],
    ['contact', 'email_address'],
  ])

  const organization = getFirstStringFromPaths(rawVet, [
    ['organization'],
    ['organizationName'],
    ['organization_name'],
    ['organization', 'name'],
    ['organization', 'organizationName'],
    ['organization', 'organization_name'],
    ['clinic'],
    ['clinicName'],
    ['clinic_name'],
    ['clinic', 'name'],
    ['hospital'],
    ['hospitalName'],
    ['hospital_name'],
    ['hospital', 'name'],
    ['institution'],
    ['institutionName'],
    ['institution_name'],
    ['institution', 'name'],
    ['user', 'clinic'],
    ['user', 'clinicName'],
    ['user', 'clinic_name'],
    ['user', 'organization'],
    ['user', 'organizationName'],
    ['user', 'organization_name'],
  ])

  if (!id && !name && !email) {
    return null
  }

  const fallbackName = name || organization || email || id
  const searchText = Array.from(
    new Set(
      [fallbackName, fullNameFromParts, organization, email, id]
        .filter(Boolean)
        .map((item) => item.toLowerCase())
        .concat(collectSearchStrings(rawVet)),
    ),
  ).join(' ')

  return {
    id: id || fallbackName,
    name: fallbackName,
    email,
    organization: organization || undefined,
    searchText,
  }
}

const CreateShareModal: React.FC<CreateShareModalProps> = ({ open, onClose }) => {
  const [activeStep, setActiveStep] = useState(0)
  const [shareMethod, setShareMethod] = useState<'registered' | 'email'>('registered')
  const [selectedVets, setSelectedVets] = useState<Vet[]>([])
  const [recipientEmails, setRecipientEmails] = useState<string[]>([''])
  const [shareType, setShareType] = useState<'all_pets' | 'specific_pets'>('all_pets')
  const [selectedPets, setSelectedPets] = useState<Pet[]>([])
  const [permissions, setPermissions] = useState(['view_metrics', 'view_emrs'])
  const [note, setNote] = useState('')
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)

  const [availableVets, setAvailableVets] = useState<Vet[]>([])
  const [userPets, setUserPets] = useState<Pet[]>([])
  const [vetsLoading, setVetsLoading] = useState(false)
  const [petsLoading, setPetsLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const notify = useNotify()
  const translate = useTranslate()

  // Fetch available vets with search support
  const fetchVets = useCallback(async (searchTerm: string = '') => {
    try {
      setVetsLoading(true)

      const trimmedSearchTerm = searchTerm.trim()
      const query = new URLSearchParams()
      if (trimmedSearchTerm) {
        query.set('search', trimmedSearchTerm)
        query.set('query', trimmedSearchTerm)
        query.set('q', trimmedSearchTerm)
      }
      // Hide Clinic-Team sub-accounts from the share picker — share is
      // with "the clinic" (the master account), not with each internal
      // vet at it. Symmetric with the AdapetApp owner-search opt-in.
      // Backend default returns sub-accounts; we opt in here.
      query.set('excludeSubAccounts', 'true')
      const queryString = query.toString()
      const url = `/api/vet-shares/available-vets${queryString ? `?${queryString}` : ''}`

      const response = await deadlineFetch(url, {
        headers: authHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch available vets')
      }

      const result = await response.json()
      const rawVets = Array.isArray(result.data) ? (result.data as Record<string, unknown>[]) : []
      const normalizedVets = rawVets
        .map((vetRecord: Record<string, unknown>) => normalizeVet(vetRecord))
        .filter((vet: Vet | null): vet is Vet => vet !== null)
      setAvailableVets(normalizedVets)
    } catch (error) {
      console.error('Failed to fetch vets:', error)
    } finally {
      setVetsLoading(false)
    }
  }, [])

  // Initial fetch of available vets
  useEffect(() => {
    if (open && shareMethod === 'registered') {
      fetchVets()
    }
  }, [open, shareMethod, fetchVets])

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  // Fetch user's pets
  useEffect(() => {
    if (open && shareType === 'specific_pets') {
      const fetchPets = async () => {
        try {
          setPetsLoading(true)
          const response = await deadlineFetch('/api/pets', {
            headers: authHeaders(),
          })

          if (!response.ok) {
            throw new Error('Failed to fetch pets')
          }

          const result = await response.json()
          setUserPets(result.docs || [])
        } catch (error) {
          console.error('Failed to fetch pets:', error)
        } finally {
          setPetsLoading(false)
        }
      }

      fetchPets()
    }
  }, [open, shareType])

  // Create share function. `creating` is owned by the BATCH (handleSubmit),
  // not by each individual call — see the comment there.
  const createShare = async (shareData: any) => {
    try {
      const response = await deadlineFetch('/api/vet-shares/create', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(shareData),
      })

      if (!response.ok) {
        let errorMessage = 'Failed to create share'
        try {
          const errorData = await response.json()
          errorMessage = errorData.error || errorData.message || errorMessage
          console.error('CreateShare Error Response:', errorData)
        } catch (e) {
          const errorText = await response.text()
          errorMessage = errorText || errorMessage
          console.error('CreateShare Error Text:', errorText)
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()

      if (!result.success) {
        const errorMessage = result.error || result.message || 'Failed to create share'
        console.error('CreateShare Failed:', result)
        throw new Error(errorMessage)
      }

      // Trigger custom event to refresh share lists
      window.dispatchEvent(new CustomEvent('shareCreated'))

      return result
    } catch (error: any) {
      console.error('CreateShare Exception:', error)
      throw error // Re-throw for caller to handle
    }
  }

  const steps = [
    translate('pos.shares.create.step_recipient'),
    translate('pos.shares.create.step_pets'),
    translate('pos.shares.create.step_permissions'),
  ]

  const handleClose = () => {
    // Clean up search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = null
    }

    setActiveStep(0)
    setShareMethod('registered')
    setSelectedVets([])
    setRecipientEmails([''])
    setShareType('all_pets')
    setSelectedPets([])
    setPermissions(['view_metrics', 'view_emrs'])
    setNote('')
    setExpiresAt(null)
    onClose()
  }

  const handleNext = () => {
    setActiveStep((prev) => prev + 1)
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleSubmit = async () => {
    console.log('HandleSubmit Debug:', {
      shareMethod,
      selectedVets,
      recipientEmails,
      shareType,
      selectedPets,
      permissions,
    })

    // Validate recipient selection
    if (shareMethod === 'registered' && selectedVets.length === 0) {
      console.log('Validation failed: No vets selected in registered mode')
      notify('pos.shares.create.error_no_vets', { type: 'error' })
      return
    }
    if (shareMethod === 'email' && !recipientEmails.some((email) => email.trim())) {
      console.log('Validation failed: No emails in email mode')
      notify('pos.shares.create.error_no_emails', { type: 'error' })
      return
    }

    // Create shares for each recipient. `creating` gates the submit button
    // for the WHOLE batch — it must stay true until every recipient's
    // request has settled, not just the fastest one (P71-2: per-call
    // `creating` re-enabled Submit the instant the first request finished
    // while siblings were still in flight, and a second click fired a whole
    // second batch — the server's find-then-create has no unique index, so
    // that meant duplicate shares / invite emails).
    setCreating(true)
    const sharePromises = []

    if (shareMethod === 'registered') {
      // Create separate share for each selected vet
      for (const vet of selectedVets) {
        const shareData: any = {
          shareType,
          permissions,
          note: note.trim() || undefined,
          expiresAt: expiresAt?.toISOString(),
          recipientId: vet.id,
        }

        if (shareType === 'specific_pets') {
          shareData.sharedPetIds = selectedPets.map((pet) => pet.id)
        }

        console.log('Creating share for vet:', vet.name, shareData)
        sharePromises.push(createShare(shareData))
      }
    } else {
      // Create separate share for each email
      const validEmails = recipientEmails.filter((email) => email.trim())
      for (const email of validEmails) {
        const shareData: any = {
          shareType,
          permissions,
          note: note.trim() || undefined,
          expiresAt: expiresAt?.toISOString(),
          recipientEmail: email.trim(),
        }

        if (shareType === 'specific_pets') {
          shareData.sharedPetIds = selectedPets.map((pet) => pet.id)
        }

        console.log('Creating share for email:', email, shareData)
        sharePromises.push(createShare(shareData))
      }
    }

    // Wait for all shares to be created
    try {
      const results = await Promise.allSettled(sharePromises)

      const successful = results.filter((r) => r.status === 'fulfilled')
      const failed = results.filter((r) => r.status === 'rejected')

      if (failed.length > 0) {
        // Log all errors for debugging
        failed.forEach((result, index) => {
          if (result.status === 'rejected') {
            console.error(`Share creation ${index + 1} failed:`, result.reason)
          }
        })

        // Show specific error message from the first failure
        const firstError = failed[0]
        const errorMessage =
          firstError.status === 'rejected' && firstError.reason instanceof Error
            ? firstError.reason.message
            : translate('pos.shares.share_creation_failed')

        if (successful.length > 0) {
          notify('pos.shares.create.partial_failure', {
            type: 'warning',
            messageArgs: { success: successful.length, failed: failed.length, error: errorMessage },
          })
        } else {
          notify('pos.shares.create.create_failed', {
            type: 'error',
            messageArgs: { error: errorMessage },
          })
        }

        // Only close if at least some succeeded
        if (successful.length > 0) {
          handleClose()
        }
      } else {
        const totalShares =
          shareMethod === 'registered'
            ? selectedVets.length
            : recipientEmails.filter((email) => email.trim()).length
        notify('pos.shares.create.created_success', {
          type: 'success',
          messageArgs: { smart_count: totalShares },
        })
        handleClose()
      }
    } catch (error) {
      console.error('Unexpected error creating shares:', error)
      const errorMessage =
        error instanceof Error ? error.message : translate('pos.shares.unexpected_error')
      notify('pos.shares.create.create_failed', {
        type: 'error',
        messageArgs: { error: errorMessage },
      })
    } finally {
      setCreating(false)
    }
  }

  const canProceedFromStep0 =
    shareMethod === 'registered'
      ? selectedVets.length > 0
      : recipientEmails.some((email) => email.trim())
  const canProceedFromStep1 = shareType === 'all_pets' || selectedPets.length > 0
  const canSubmit = permissions.length > 0 && canProceedFromStep0 && canProceedFromStep1

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">
                {translate('pos.shares.create.how_to_share')}
              </FormLabel>
              <RadioGroup
                value={shareMethod}
                onChange={(e) => setShareMethod(e.target.value as 'registered' | 'email')}
              >
                <FormControlLabel
                  value="registered"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <PersonIcon />
                      {translate('pos.shares.create.share_registered')}
                    </Box>
                  }
                />
                <FormControlLabel
                  value="email"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <EmailIcon />
                      {translate('pos.shares.create.invite_email')}
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {shareMethod === 'registered' ? (
              <Autocomplete
                multiple
                options={availableVets}
                getOptionLabel={(option) =>
                  [option.name, option.organization, option.email].filter(Boolean).join(' - ')
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedVets}
                onChange={(_, newValue) => setSelectedVets(newValue)}
                loading={vetsLoading}
                filterOptions={(options, { inputValue }) => {
                  const keyword = inputValue.trim().toLowerCase()
                  if (!keyword) {
                    return options
                  }
                  const tokens = keyword.split(/\s+/).filter(Boolean)
                  return options.filter((option) =>
                    tokens.every((token) => option.searchText.includes(token)),
                  )
                }}
                onInputChange={(_, newInputValue) => {
                  // Clear previous timeout
                  if (searchTimeoutRef.current) {
                    clearTimeout(searchTimeoutRef.current)
                  }

                  // Debounce the search to avoid too many API calls
                  searchTimeoutRef.current = setTimeout(() => {
                    fetchVets(newInputValue)
                  }, 300)
                }}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography>{option.name || option.email || option.id}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[option.organization, option.email].filter(Boolean).join(' • ')}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={[option.name, option.email].filter(Boolean).join(' - ')}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('pos.shares.create.select_veterinarians')}
                    placeholder={translate('pos.shares.create.search_vets_placeholder')}
                    fullWidth
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {vetsLoading && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <Box>
                {recipientEmails.map((email, index) => (
                  <Box
                    key={index}
                    display="flex"
                    gap={1}
                    mb={index < recipientEmails.length - 1 ? 2 : 0}
                  >
                    <TextField
                      label={translate('pos.shares.create.vet_email_n', { n: index + 1 })}
                      type="email"
                      value={email}
                      onChange={(e) => {
                        const newEmails = [...recipientEmails]
                        newEmails[index] = e.target.value
                        setRecipientEmails(newEmails)
                      }}
                      fullWidth
                      placeholder={translate('pos.shares.create.vet_email_placeholder')}
                      helperText={index === 0 ? translate('pos.shares.create.invite_helper') : ''}
                    />
                    {recipientEmails.length > 1 && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => {
                          const newEmails = recipientEmails.filter((_, i) => i !== index)
                          setRecipientEmails(newEmails)
                        }}
                        sx={{ minWidth: '40px', height: '56px' }}
                      >
                        ✕
                      </Button>
                    )}
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  onClick={() => setRecipientEmails([...recipientEmails, ''])}
                  sx={{ mt: 1 }}
                  startIcon={<EmailIcon />}
                >
                  {translate('pos.shares.create.add_another_email')}
                </Button>
              </Box>
            )}
          </Box>
        )

      case 1:
        return (
          <Box>
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">
                {translate('pos.shares.create.what_to_share')}
              </FormLabel>
              <RadioGroup
                value={shareType}
                onChange={(e) => setShareType(e.target.value as 'all_pets' | 'specific_pets')}
              >
                <FormControlLabel
                  value="all_pets"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography>{translate('pos.shares.create.all_pets')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('pos.shares.create.all_pets_hint')}
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="specific_pets"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography>{translate('pos.shares.create.specific_pets')}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('pos.shares.create.specific_pets_hint')}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>

            {shareType === 'specific_pets' && (
              <Autocomplete
                multiple
                options={userPets}
                getOptionLabel={(option) =>
                  `${option.name || `Pet #${option.id}`} (${option.species})`
                }
                value={selectedPets}
                onChange={(_, newValue) => setSelectedPets(newValue)}
                loading={petsLoading}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name || translate('pos.common.pet_fallback', { id: option.id })}
                      {...getTagProps({ index })}
                      key={option.id}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('pos.shares.create.select_pets')}
                    placeholder={translate('pos.shares.create.select_pets_placeholder')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {petsLoading && <CircularProgress size={20} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          </Box>
        )

      case 2:
        return (
          <Box>
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend">
                <Box display="flex" alignItems="center" gap={1}>
                  <SecurityIcon />
                  {translate('pos.shares.create.what_can_they_do')}
                </Box>
              </FormLabel>
              <FormGroup>
                {[
                  {
                    value: 'view_metrics',
                    label: translate('pos.shares.create.perm_view_metrics'),
                  },
                  { value: 'view_emrs', label: translate('pos.shares.create.perm_view_emrs') },
                  {
                    value: 'add_activities',
                    label: translate('pos.shares.create.perm_add_activities'),
                  },
                  {
                    value: 'add_emr_notes',
                    label: translate('pos.shares.create.perm_add_emr_notes'),
                  },
                ].map((permission) => (
                  <FormControlLabel
                    key={permission.value}
                    control={
                      <Checkbox
                        checked={permissions.includes(permission.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPermissions([...permissions, permission.value])
                          } else {
                            setPermissions(permissions.filter((p) => p !== permission.value))
                          }
                        }}
                      />
                    }
                    label={permission.label}
                  />
                ))}
              </FormGroup>
            </FormControl>

            <TextField
              label={translate('pos.shares.create.note_label')}
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              fullWidth
              placeholder={translate('pos.shares.create.note_placeholder')}
              sx={{ mb: 3 }}
            />

            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={translate('pos.shares.create.expiration_label')}
                value={expiresAt}
                onChange={(newValue) => {
                  // Ignore mid-edit Invalid Date objects (MUI emits one while a
                  // field is being typed) so we never store NaN.
                  if (newValue && Number.isNaN(newValue.getTime())) return
                  setExpiresAt(newValue)
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: translate('pos.shares.create.expiration_hint'),
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        )

      default:
        return null
    }
  }

  return (
    <Dialog open={open} onClose={creating ? undefined : handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate('pos.shares.create.title')}</DialogTitle>

      <DialogContent>
        {/* Labels under the step numbers: side by side, "Set Permissions"
            was cut off on phones. */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>{translate('pos.shares.create.cancel')}</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>{translate('pos.shares.create.back')}</Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && !canProceedFromStep0) ||
              (activeStep === 1 && !canProceedFromStep1)
            }
          >
            {translate('pos.shares.create.next')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!canSubmit || creating}
            startIcon={creating && <CircularProgress size={20} />}
          >
            {shareMethod === 'email'
              ? translate('pos.shares.create.send_invitations')
              : translate('pos.shares.create.create_shares')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default CreateShareModal
